// src/components/ObslugaPrawnaBiznesu.js

import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Scale, FileText, Users, Briefcase, AlertCircle, CheckCircle, ArrowRight, ChevronRight } from 'lucide-react';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const ObslugaPrawnaBiznesu = () => {
  const services = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Umowy handlowe",
      description: "Sporządzanie, negocjowanie i weryfikacja umów handlowych, kontraktów B2B oraz dokumentacji korporacyjnej."
    },
    {
      icon: <Scale className="w-8 h-8" />,
      title: "Spory gospodarcze",
      description: "Reprezentacja w sporach sądowych, arbitrażowych oraz mediacje biznesowe z naciskiem na ochronę interesów firmy."
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "Prawo spółek",
      description: "Zakładanie spółek, przekształcenia, fuzje i przejęcia. Kompleksowa obsługa zgromadzeń wspólników i rad nadzorczych."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Compliance",
      description: "Audyty prawne, wdrażanie procedur compliance, ochrona danych osobowych (RODO) i tajemnicy przedsiębiorstwa."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Prawo pracy",
      description: "Regulaminy pracy, umowy o pracę, zwolnienia grupowe, spory pracownicze i reprezentacja przed sądami pracy."
    },
    {
      icon: <AlertCircle className="w-8 h-8" />,
      title: "Windykacja",
      description: "Skuteczna windykacja należności, postępowania egzekucyjne oraz zabezpieczenie wierzytelności."
    }
  ];

  const benefits = [
    "Stały dostęp do prawnika bez konieczności zatrudniania",
    "Przewidywalne koszty obsługi prawnej",
    "Szybka reakcja na bieżące problemy",
    "Minimalizacja ryzyka prawnego",
    "Wsparcie w negocjacjach biznesowych",
    "Reprezentacja w sporach sądowych"
  ];

  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 animate-float">
            <Scale className="w-32 h-32 text-violet-600" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float animation-delay-2000">
            <Shield className="w-24 h-24 text-violet-500" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-black text-white mb-6 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Kompleksowa obsługa prawna dla biznesu
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
              Zapewniamy pełne wsparcie prawne dla Twojej firmy. Od sporządzania umów, przez bieżące doradztwo, po reprezentację w sporach - jesteśmy z Tobą na każdym etapie rozwoju biznesu.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleContactScroll}
              className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-lg font-semibold hover:shadow-xl hover:shadow-violet-600/25 hover:scale-105 transition-all duration-300"
            >
              <span className="flex items-center justify-center gap-2">
                Zamów konsultację
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
            <Link 
              to="/"
              className="px-8 py-4 bg-black/20 backdrop-blur-sm border border-white/20 rounded-lg text-white font-semibold hover:bg-white/10 transition-all duration-300 text-center"
            >
              Wróć do strony głównej
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Nasze usługi prawne
            </h2>
            <p className="text-lg text-gray-400">
              Kompleksowe wsparcie we wszystkich aspektach prawnych Twojego biznesu
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group backdrop-blur-md bg-white/5 rounded-xl p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-600/10"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <span className="text-violet-600">
                    {service.icon}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-600/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-6 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
                Dlaczego warto nam zaufać?
              </h2>
              <p className="text-lg text-gray-400 mb-8">
                Nasza kancelaria specjalizuje się w obsłudze przedsiębiorców. Rozumiemy specyfikę prowadzenia biznesu i wiemy, jak ważne jest szybkie i skuteczne rozwiązywanie problemów prawnych.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-violet-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="/mlotek2.jpg" 
                  alt="Obsługa prawna biznesu"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="backdrop-blur-md bg-white/5 rounded-2xl p-12 border border-white/10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Potrzebujesz wsparcia prawnego?
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              Skontaktuj się z nami i dowiedz się, jak możemy pomóc w rozwoju Twojego biznesu
            </p>
            <button 
              onClick={handleContactScroll}
              className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-lg font-semibold hover:shadow-xl hover:shadow-violet-600/25 hover:scale-105 transition-all duration-300"
            >
              <span className="flex items-center justify-center gap-2">
                Umów bezpłatną konsultację
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default ObslugaPrawnaBiznesu;